// Saving the open image's annotations: a port of AnnotationCanvas._save_annotations, with two
// differences that only matter when something goes wrong: shapes this version cannot show are never
// deleted, and writes are serialised so an edit made during a save is saved by the next one.
import type { AnnotationOut, AnnotationWrite } from "../api/annotations";
import { APIError } from "../api/errors";
import { coordinatesOf, type Ann, type AnnotationDoc } from "./doc";

export interface SaveApi {
  list(imageId: number): Promise<AnnotationOut[]>;
  create(imageId: number, a: { class_id: number | null; shape_type: string; coordinates: unknown; source: string }): Promise<AnnotationOut>;
  update(id: number, patch: AnnotationWrite): Promise<AnnotationOut>;
  remove(id: number): Promise<void>;
}

/** Calls in flight at once. The desktop does them one at a time; order does not matter to the server. */
const CONCURRENCY = 6;

async function pool<T>(items: T[], fn: (t: T) => Promise<void>) {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, async () => {
    while (i < items.length) await fn(items[i++]);
  }));
}

/** Returns true when the image's annotations are saved. Messages go to `doc.say`, as the desktop's status label. */
export async function saveAnnotations(doc: AnnotationDoc, imageId: number, api: SaveApi): Promise<boolean> {
  if (!doc.dirty) return true;
  const version = doc.version;
  try {
    const existing = await api.list(imageId);
    const current = doc.anns.slice();
    const have = new Set(current.map((a) => a.serverId).filter((x): x is number => x !== null));
    const gone = existing.map((e) => e.id).filter((id) => !have.has(id) && !doc.hidden.has(id));
    await pool(gone, async (id) => { try { await api.remove(id); } catch (e) { if (!(e instanceof APIError)) throw e; } });

    const create = async (a: Ann) => {
      const made = await api.create(imageId, { class_id: a.classId, shape_type: a.kind, coordinates: coordinatesOf(a), source: a.source });
      a.serverId = made.id;
      a.reviewed = made.reviewed ?? false;
    };
    // Existing shapes are updated in parallel. New ones are created one at a time, in the order they
    // were drawn: the server numbers them as they arrive, and that order is the list order after a reload.
    await pool(current.filter((a) => a.serverId !== null), async (a) => {
      try {
        await api.update(a.serverId!, { coordinates: coordinatesOf(a), class_id: a.classId, reviewed: a.reviewed });
      } catch (e) {
        // The record was deleted by an earlier save and the delete has since been undone: make it again.
        if (e instanceof APIError && e.status === 404) { a.serverId = null; return; }
        throw e;
      }
    });
    for (const a of current) if (a.serverId === null) await create(a);
    doc.dirty = doc.version !== version;                               // an edit made during the save keeps it dirty
    doc.say("Saved");
    doc.touch();
    return true;
  } catch (e) {
    doc.say(`Save error: ${(e as Error).message}`);
    return false;
  }
}

/** One save at a time per image; a request that arrives while one is running triggers one more afterwards. */
export class Saver {
  private running: Promise<boolean> | null = null;
  private again = false;
  constructor(private doc: AnnotationDoc, private imageId: () => number | null, private api: SaveApi) {}

  save(): Promise<boolean> {
    if (this.running) { this.again = true; return this.running; }
    const id = this.imageId();
    if (id === null) return Promise.resolve(true);
    this.running = (async () => {
      try {
        let ok = await saveAnnotations(this.doc, id, this.api);
        while (ok && this.again && this.doc.dirty) { this.again = false; ok = await saveAnnotations(this.doc, id, this.api); }
        return ok;
      } finally { this.running = null; this.again = false; }
    })();
    return this.running;
  }
}
